import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { TransactionSigningRequired } from '../api';
import keycloak from '../keycloak';
import theme from '../theme';
import { Card, Button, Input, Select, Money, Spinner, MaskedAccount, MaskedSortCode, Badge } from '../components/common';

const STEPS = ['Account', 'Beneficiary', 'Details', 'Review'];

export default function NewPayment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    sourceAccountId: '',
    beneficiaryId: '',
    amount: '',
    reference: '',
    scheme: 'FPS',
    currency: 'GBP',
  });

  useEffect(() => {
    Promise.all([
      api.get('/accounts'),
      api.get('/beneficiaries'),
      api.get('/payment-limits'),
    ]).then(([acc, ben, lim]) => {
      setAccounts(acc.filter(a => a.status === 'ACTIVE'));
      setBeneficiaries(ben.filter(b => b.status === 'ACTIVE'));
      setLimits(lim);
      if (acc.length === 1) setForm(f => ({ ...f, sourceAccountId: acc[0].id }));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const selectedAccount = accounts.find(a => a.id === form.sourceAccountId);
  const selectedBeneficiary = beneficiaries.find(b => b.id === form.beneficiaryId);
  const amountPounds = parseFloat(form.amount) || 0;
  const dailyRemaining = limits ? limits.daily_aggregate_max - limits.daily_used : Infinity;

  const submitPayment = async (signingToken = null) => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        sourceAccountId: form.sourceAccountId,
        beneficiaryId: form.beneficiaryId,
        amount: amountPounds,
        currency: form.currency,
        scheme: form.scheme,
        reference: form.reference,
      };
      const idempKey = `portal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const payment = await api.initiatePayment(body, signingToken, idempKey);
      setResult(payment);
    } catch (err) {
      if (err instanceof TransactionSigningRequired) {
        // Need re-authentication for SCA
        try {
          // Store payment intent in sessionStorage so we can resume after re-auth
          sessionStorage.setItem('pendingPayment', JSON.stringify(form));
          // Force fresh login
          await keycloak.login({ prompt: 'login', maxAge: 0 });
        } catch {
          setError('Re-authentication was cancelled or failed.');
        }
      } else {
        setError(err.message || 'Payment failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Check for pending payment after re-auth
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingPayment');
    if (pending) {
      sessionStorage.removeItem('pendingPayment');
      const savedForm = JSON.parse(pending);
      setForm(savedForm);
      setStep(3); // Go to review step
      // Auto-submit with the fresh token
      const body = {
        sourceAccountId: savedForm.sourceAccountId,
        beneficiaryId: savedForm.beneficiaryId,
        amount: parseFloat(savedForm.amount) || 0,
        currency: savedForm.currency,
        scheme: savedForm.scheme,
        reference: savedForm.reference,
      };
      const idempKey = `portal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      api.initiatePayment(body, keycloak.token, idempKey)
        .then(setResult)
        .catch(err => setError(err.message));
    }
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}><Spinner size={32} /></div>;

  // Success screen
  if (result) {
    const needsApproval = result.status === 'PENDING_APPROVAL' || result.status === 'VALIDATED';
    return (
      <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', paddingTop: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>{result.status === 'FAILED' ? '\u2716' : '\u2714'}</div>
        <h2 style={{ margin: '0 0 8px' }}>
          {result.status === 'FAILED' ? 'Payment failed' : needsApproval ? 'Payment pending approval' : 'Payment submitted'}
        </h2>
        <p style={{ color: theme.colors.textLight, fontSize: '14px' }}>
          {needsApproval
            ? 'Your payment requires bank approval before it can be processed.'
            : result.status === 'FAILED'
            ? 'The payment could not be processed.'
            : 'Your payment has been submitted for processing.'}
        </p>
        <Card style={{ textAlign: 'left', marginTop: '24px' }}>
          <div style={summaryRow}><span>Amount</span><Money amount={result.amount} currency={result.currency} /></div>
          <div style={summaryRow}><span>Reference</span><span>{result.reference}</span></div>
          <div style={summaryRow}><span>Status</span><Badge color={result.status === 'FAILED' ? 'danger' : 'info'}>{result.status}</Badge></div>
          <div style={summaryRow}><span>Payment ID</span><span style={{ fontFamily: theme.fonts.mono, fontSize: '12px' }}>{result.id}</span></div>
        </Card>
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Button variant="outline" onClick={() => navigate('/payments')}>View payments</Button>
          <Button variant="primary" onClick={() => { setResult(null); setStep(0); setForm({ sourceAccountId: accounts[0]?.id || '', beneficiaryId: '', amount: '', reference: '', scheme: 'FPS', currency: 'GBP' }); }}>Make another</Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <Button variant="ghost" size="sm" onClick={() => navigate('/payments')} style={{ marginBottom: '16px' }}>&larr; Back</Button>
      <h1 style={{ fontSize: '20px', marginBottom: '4px' }}>New payment</h1>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{
            flex: 1, height: '4px', borderRadius: '2px',
            background: i <= step ? theme.colors.accent : theme.colors.border,
          }} />
        ))}
      </div>

      {error && (
        <Card style={{ background: '#FED7D7', borderColor: theme.colors.danger, marginBottom: '16px' }}>
          <div style={{ color: '#742A2A', fontSize: '14px' }}>{error}</div>
        </Card>
      )}

      {/* Step 0: Select Account */}
      {step === 0 && (
        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Select account to pay from</h3>
          {accounts.map(acc => (
            <div
              key={acc.id}
              onClick={() => { set('sourceAccountId', acc.id); setStep(1); }}
              style={{
                padding: '16px',
                border: `2px solid ${form.sourceAccountId === acc.id ? theme.colors.accent : theme.colors.border}`,
                borderRadius: theme.radius,
                marginBottom: '8px',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600 }}>{acc.name}</div>
              <div style={{ fontSize: '13px', color: theme.colors.textLight }}>{acc.accountNumber} &middot; {acc.currency}</div>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>
                Available: <Money amount={acc.balance?.available || 0} currency={acc.currency} />
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Step 1: Select Beneficiary */}
      {step === 1 && (
        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Select beneficiary</h3>
          {beneficiaries.length === 0 ? (
            <div>
              <p style={{ color: theme.colors.textLight, fontSize: '14px' }}>No beneficiaries saved.</p>
              <Button variant="accent" onClick={() => navigate('/beneficiaries')}>Add a beneficiary</Button>
            </div>
          ) : (
            <>
              {beneficiaries.map(b => (
                <div
                  key={b.id}
                  onClick={() => { set('beneficiaryId', b.id); setStep(2); }}
                  style={{
                    padding: '12px 16px',
                    border: `2px solid ${form.beneficiaryId === b.id ? theme.colors.accent : theme.colors.border}`,
                    borderRadius: theme.radius,
                    marginBottom: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{b.nickname || b.name}</div>
                  <div style={{ fontSize: '13px', color: theme.colors.textLight }}>
                    <MaskedSortCode value={b.sortCode} /> &middot; <MaskedAccount value={b.accountNumber} />
                  </div>
                </div>
              ))}
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <Button variant="ghost" size="sm" onClick={() => navigate('/beneficiaries')}>Add new beneficiary</Button>
              </div>
            </>
          )}
          <div style={{ marginTop: '16px' }}>
            <Button variant="ghost" size="sm" onClick={() => setStep(0)}>&larr; Back</Button>
          </div>
        </Card>
      )}

      {/* Step 2: Amount and details */}
      {step === 2 && (
        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Payment details</h3>
          <Input
            label="Amount (GBP)"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={form.amount}
            onChange={e => set('amount', e.target.value)}
            error={amountPounds > (limits?.single_payment_max || Infinity) ? `Exceeds single payment limit of \u00A3${limits.single_payment_max?.toLocaleString()}` : null}
          />
          {limits && (
            <div style={{ fontSize: '12px', color: theme.colors.textMuted, marginTop: '-8px', marginBottom: '12px' }}>
              Daily remaining: <strong>&pound;{Math.max(0, dailyRemaining).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</strong>
            </div>
          )}
          <Input
            label="Reference"
            placeholder="e.g. Invoice 12345"
            value={form.reference}
            onChange={e => set('reference', e.target.value)}
          />
          <Select label="Payment scheme" value={form.scheme} onChange={e => set('scheme', e.target.value)}>
            <option value="FPS">Faster Payments (FPS)</option>
            <option value="BACS">BACS</option>
            <option value="CHAPS">CHAPS</option>
          </Select>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '8px' }}>
            <Button variant="ghost" onClick={() => setStep(1)}>&larr; Back</Button>
            <Button
              variant="accent"
              disabled={!form.amount || parseFloat(form.amount) <= 0 || !form.reference}
              onClick={() => setStep(3)}
            >
              Review payment
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3: Review and confirm */}
      {step === 3 && (
        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Review payment</h3>
          <div style={summaryRow}>
            <span>From</span>
            <span>{selectedAccount?.name} ({selectedAccount?.accountNumber})</span>
          </div>
          <div style={summaryRow}>
            <span>To</span>
            <span>{selectedBeneficiary?.nickname || selectedBeneficiary?.name}</span>
          </div>
          <div style={summaryRow}>
            <span>Amount</span>
            <span style={{ fontSize: '18px', fontWeight: 700 }}><Money amount={amountPounds} currency={form.currency} /></span>
          </div>
          <div style={summaryRow}>
            <span>Reference</span>
            <span>{form.reference}</span>
          </div>
          <div style={summaryRow}>
            <span>Scheme</span>
            <span>{form.scheme}</span>
          </div>
          {amountPounds > (limits?.requires_bank_approval_above || Infinity) && (
            <div style={{ padding: '12px', background: '#FEFCBF', borderRadius: theme.radiusSm, marginTop: '12px', fontSize: '13px', color: '#744210' }}>
              This payment exceeds &pound;{limits.requires_bank_approval_above.toLocaleString()} and will require bank approval before processing.
            </div>
          )}
          {amountPounds > (limits?.requires_totp_above || Infinity) && (
            <div style={{ padding: '12px', background: '#BEE3F8', borderRadius: theme.radiusSm, marginTop: '12px', fontSize: '13px', color: '#2A4365' }}>
              For security, you will be asked to re-authenticate to confirm this payment.
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '24px' }}>
            <Button variant="ghost" onClick={() => setStep(2)}>&larr; Edit</Button>
            <Button variant="primary" loading={submitting} onClick={() => submitPayment()}>
              Confirm payment
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

const summaryRow = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '10px 0',
  borderBottom: `1px solid ${theme.colors.borderLight}`,
  fontSize: '14px',
};
